<template>
  <div class="file-progress-tracker">
    <!-- ProgressBar principal con steps -->
    <ProgressBar 
      v-if="file && (loading || progressPercentage >= 100)"
      :file="file"
      :percentage="progressPercentage"
      :text="progressText"
      :steps="processingSteps"
      :elapsedTime="getProgressInfo().elapsedTime"
      :estimatedTime="estimatedTime"
      :progressRatio="progressRatio"
      :loading="loading"
      @cancel-and-reset="handleCancelAndReset"
    />
  </div>
</template>

<script>
import ProgressBar from './ProgressBar.vue'

export default {
  name: 'FileProgressTracker',
  components: {
    ProgressBar
  },
  props: {
    file: {
      type: File,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      progressPercentage: 0,
      progressText: "Iniciando análisis...",
      startTime: null,
      progressInterval: null,
      progressTimeout: null,
      progressRatio: 0,
      processingSteps: [
        {
          title: "Leyendo PDF",
          subtitle: "Cargando archivo PDF",
          icon: "fa-file-pdf-o",
          isActive: true,
          isCompleted: false,
          weight: 0.30
        },
        {
          title: "Procesando con IA",
          subtitle: "Analizando información del documento",
          icon: "fa-magic",
          isActive: false,
          isCompleted: false,
          weight: 0.50
        },
        {
          title: "Validando datos",
          subtitle: "Verificando información extraída",
          icon: "fa-check-circle",
          isActive: false,
          isCompleted: false,
          weight: 0.20
        }
      ]
    }
  },
  watch: {
    loading: {
      handler(newVal, oldVal) {
        if (newVal && !oldVal) {
          // Iniciar cuando comienza la carga
          this.startProgressTracking();
        } else if (!newVal && oldVal) {
          // Completar cuando termina la carga
          this.handleProcessingComplete();
        }
      },
      immediate: true
    },

  },

  computed: {    
    estimatedTime() {
      return this.getProgressInfo().estimatedTime;
    }
  },

  methods: {
    startProgressTracking() {
      this.startTime = Date.now();
      this.progressPercentage = 0;
      this.progressText = "Iniciando análisis...";
      this.progressRatio = 0;
      
      // Resetear todos los pasos al estado inicial
      this.processingSteps.forEach((step, index) => {
        step.isActive = index === 0;
        step.isCompleted = false;
      });
      
      this.startRealProgressTracking();
    },

    startRealProgressTracking() {
      if (!this.file) return;
      
      const totalEstimatedTime = this.calculateEstimatedTime();
      const updateInterval = Math.max(200, Math.min(1000, totalEstimatedTime * 20));
      
      this.progressInterval = setInterval(() => {
        this.updateProgressRealTime(totalEstimatedTime);
      }, updateInterval);
      
      this.progressTimeout = setTimeout(() => {
        if (this.progressPercentage < 100) {
          this.handleTimeout();
        }
      }, (totalEstimatedTime + 10) * 1000);
    },

    calculateEstimatedTime() {
      const fileSizeMB = this.file ? (this.file.size / (1024 * 1024)) : 0;
      let baseTimePerMB = 2.0;
      
      if (this.file && this.file.type === 'application/pdf') {
        baseTimePerMB = 2.5;
      }
      
      if (fileSizeMB > 10) {
        baseTimePerMB = 1.5;
      } else if (fileSizeMB < 1) {
        baseTimePerMB = 4.0;
      }
      
      const baseProcessingTime = 8;
      const minTime = 5;
      const maxTime = 120;
      
      let estimatedTime = (fileSizeMB * baseTimePerMB) + baseProcessingTime;
      return Math.max(minTime, Math.min(maxTime, estimatedTime));
    },

    updateProgressRealTime(totalEstimatedTime) {
      const elapsedTime = (Date.now() - this.startTime) / 1000;
      this.progressRatio = Math.min(elapsedTime / totalEstimatedTime, 0.95);
      this.progressPercentage = Math.floor(this.progressRatio * 100);
      
      this.updateProgressText(this.progressRatio);
      this.syncStepsWithProgress(); // Sincronizar pasos con el progreso
    },

    updateProgressText(progressRatio) {
      if (progressRatio < 0.25) {
        this.progressText = "Leyendo PDF y extrayendo texto...";
        this.updateStepStatus(0, true, false);
      } else if (progressRatio < 0.75) {
        this.progressText = "Procesando con Inteligencia Artificial...";
        this.updateStepStatus(0, false, true);
        this.updateStepStatus(1, true, false);
      } else if (progressRatio < 0.95) {
        this.progressText = "Validando datos extraídos...";
        this.updateStepStatus(0, false, true);
        this.updateStepStatus(1, false, true);
        this.updateStepStatus(2, true, false);
      } else {
        this.progressText = "Finalizando procesamiento...";
        this.updateStepStatus(0, false, true);
        this.updateStepStatus(1, false, true);
        this.updateStepStatus(2, false, true);
      }
    },

    updateStepStatus(stepIndex, isActive, isCompleted) {
      if (this.processingSteps[stepIndex]) {
        this.processingSteps[stepIndex].isActive = isActive;
        this.processingSteps[stepIndex].isCompleted = isCompleted;
      }
    },

    // Método para manejar la finalización del procesamiento
    handleProcessingComplete() {
      this.completeProgress();
      this.syncStepsWithProgress();
    },

    // Método para sincronizar pasos con el progreso
    syncStepsWithProgress() {
      const ratio = this.progressRatio;
      
      if (ratio < 0.25) {
        // Primer paso activo
        this.processingSteps.forEach((step, index) => {
          step.isActive = index === 0;
          step.isCompleted = false;
        });
      } else if (ratio < 0.75) {
        // Primer paso completado, segundo activo
        this.processingSteps.forEach((step, index) => {
          if (index === 0) {
            step.isActive = false;
            step.isCompleted = true;
          } else if (index === 1) {
            step.isActive = true;
            step.isCompleted = false;
          } else {
            step.isActive = false;
            step.isCompleted = false;
          }
        });
      } else if (ratio < 0.95) {
        // Primeros dos pasos completados, tercero activo
        this.processingSteps.forEach((step, index) => {
          if (index < 2) {
            step.isActive = false;
            step.isCompleted = true;
          } else if (index === 2) {
            step.isActive = true;
            step.isCompleted = false;
          }
        });
      } else {
        // Todos completados
        this.processingSteps.forEach((step) => {
          step.isActive = false;
          step.isCompleted = true;
        });
      }
    },

    getProgressInfo() {
      if (!this.startTime) return { elapsedTime: 0, estimatedTime: 0 };
      
      const elapsedTime = (Date.now() - this.startTime) / 1000;
      return {
        elapsedTime: elapsedTime.toFixed(1),
        estimatedTime: this.calculateEstimatedTime().toFixed(1)
      };
    },

    handleTimeout() {
      this.progressText = "La operación está tomando más tiempo del esperado...";
      this.progressPercentage = Math.min(95, this.progressPercentage + 2);
    },

    completeProgress() {      
      this.progressPercentage = 100;
      this.progressText = "¡Análisis completado!";
      this.progressRatio = 1;
      
      // Marcar todos los pasos como completados
      this.processingSteps.forEach((step) => {
        step.isActive = false;
        step.isCompleted = true;
      });
      
      this.$emit('processing-completed');
    },

    handleCancelAndReset() {
      this.reset();
      this.$emit('cancel-and-reset');
    },

    reset() {
      this.progressPercentage = 0;
      this.progressText = "Iniciando análisis...";
      this.progressRatio = 0;
      this.startTime = null;
      
      // Resetear todos los pasos al estado inicial
      this.processingSteps.forEach((step, index) => {
        step.isActive = index === 0;
        step.isCompleted = false;
      });
    }
  }
}
</script>

<style scoped>
.progress-info-content {
  margin-bottom: 10px;
}

.progress-info-content small {
  font-size: 0.85rem;
  font-weight: 500;
}

.progress-info-content i {
  color: #007bff;
}

.progress-info-content .mx-2 {
  margin: 0 8px;
  color: #6c757d;
}
</style> 